import type { UUID } from '@aline/common';
import { PermissionRole } from '@aline/common';
import type { AvatarInfo } from 'src/components/AlineAvatar';
import { toAvatarPlaceholder } from 'src/components/AlineAvatar';
import {
  LAUNCHER_PLACEHOLDER_TEXT,
  WORKFLOW_LAUNCHER_LABEL,
} from 'src/components/workflow/workflow-helpers';
import type { Apollo } from 'src/gql/generated';
import { getEntityDisplayDetails } from 'src/helpers/accounts';
import { appColors, roleAvatarColor } from 'src/theme';
import type { AccountSubset } from 'src/types';

export function userComparator(
  userA: Pick<Apollo.User, 'firstName' | 'email'>,
  userB: Pick<Apollo.User, 'firstName' | 'email'>
): number {
  const compareStringA = userA.firstName ?? userA.email;
  const compareStringB = userB.firstName ?? userB.email;

  return compareStringA.localeCompare(compareStringB);
}

export function compareByEmail(
  userA: Pick<Apollo.User, 'email' | '__typename'>,
  userB: Pick<Apollo.User, 'email' | '__typename'>
): number {
  return userA.email.localeCompare(userB.email);
}

export function userWithPlural(userIds: UUID[]): string {
  return userIds.length <= 1 ? 'user' : 'users';
}

export const permissionsAscOrder: PermissionRole[] = [
  PermissionRole.Viewer,
  PermissionRole.Commenter,
  PermissionRole.Suggester,
  PermissionRole.Editor,
  PermissionRole.Owner,
];

interface DisplayDetails {
  primaryText: string;
  avatarColor?: string;
  secondaryText?: string;
}

export function getRoleDisplayDetails(role: { roleName: string }): DisplayDetails {
  return { avatarColor: roleAvatarColor, primaryText: role.roleName };
}

export function getAssigneeDisplayDetails(
  assignee?: AccountSubset | null,
  assigneeRoleName?: string | null
): ReturnType<typeof getEntityDisplayDetails> {
  const assigneeDetails = assignee ? getEntityDisplayDetails(assignee) : null;
  const assigneeRoleDetails = assigneeRoleName
    ? { avatarColor: appColors.iconsType, primary: assigneeRoleName }
    : null;

  return assigneeDetails || assigneeRoleDetails || { primary: 'Unassigned' };
}

interface PermissionWithAccountAndRole extends Pick<
  Apollo.WorkflowPermission,
  'avatarColor' | 'isLauncher'
> {
  account: AccountSubset | null;
  roleName: string | null;
}

/**
 * Who a permission stands for, as a name and avatar color. A template's launcher placeholder has
 * no user or role, so it goes by its flag rather than falling through to "Unassigned".
 */
export function getPermissionAssigneeDisplayDetails(
  permission?: PermissionWithAccountAndRole | null
): ReturnType<typeof getEntityDisplayDetails> {
  if (permission?.isLauncher)
    return {
      avatarColor: roleAvatarColor,
      primary: WORKFLOW_LAUNCHER_LABEL,
      secondary: LAUNCHER_PLACEHOLDER_TEXT,
    };
  return getAssigneeDisplayDetails(permission?.account, permission?.roleName);
}

/** What the avatar for a permission shows: the account, else the role, else the launcher's initial. */
export function getPermissionAvatarInfo(
  permission?: PermissionWithAccountAndRole | null
): AvatarInfo {
  if (permission?.isLauncher)
    return toAvatarPlaceholder(WORKFLOW_LAUNCHER_LABEL, permission.avatarColor);
  if (permission?.account) return permission.account;
  return getRoleAvatarInfo(permission?.roleName, permission?.avatarColor ?? null);
}

interface RecipientAssignment {
  assignee: AccountSubset | null;
  assigneeRoleName: string | null;
  permission: Pick<Apollo.WorkflowPermission, 'avatarColor' | 'isLauncher'>;
}

/**
 * Who a recipient (a signer, a cc, a field's action) stands for. On a template, the launcher's
 * recipient has no assignee or role until launch, so it is named by its permission's flag.
 */
export function getRecipientAssigneeDisplayDetails(
  recipient?: RecipientAssignment | null
): ReturnType<typeof getEntityDisplayDetails> {
  return getPermissionAssigneeDisplayDetails(toRecipientPermission(recipient));
}

export function getRecipientAvatarInfo(recipient?: RecipientAssignment | null): AvatarInfo {
  return getPermissionAvatarInfo(toRecipientPermission(recipient));
}

/** A role is a seat for someone not yet assigned, so its avatar is a placeholder. */
export function getRoleAvatarInfo(
  roleName: string | null | undefined,
  color: string | null
): AvatarInfo {
  return roleName ? toAvatarPlaceholder(roleName, color) : null;
}

export function getPermissionUser(permission?: PermissionWithAccountAndRole | null) {
  const account = permission?.account;
  if (account?.__typename === 'User') return account;
  return null;
}

function toRecipientPermission(
  recipient?: RecipientAssignment | null
): PermissionWithAccountAndRole | null {
  if (!recipient) return null;
  return {
    account: recipient.assignee,
    avatarColor: recipient.permission.avatarColor,
    isLauncher: recipient.permission.isLauncher,
    roleName: recipient.assigneeRoleName,
  };
}
